import { doc, updateDoc, writeBatch } from 'firebase/firestore';
import { db } from '../firebase';
import { backToDay, go, openRoute, openStop, ui } from '../app/ui';
import { ago, dayTime } from '../app/words';
import type { Model } from '../data/model';
import type { Note } from '../data/live';
import { track } from '../data/analytics';
import { EmptyHelp } from './bits';
import { Icon } from './Icon';

/**
 * The owner's notifications (`notifications/{uid}/items`, written by functions/src/notices.ts), in a panel under the
 * bell, as the phone lists them. Opening one marks it read and goes where the phone's goes: Live, with its route or stop
 * open when it names one, or Couriers for someone joining or leaving.
 */
const ICONS: Record<string, string> = {
  routeStarted: 'path', routeCompleted: 'flagCheckered', stopCompleted: 'checkCircle', podCaptured: 'camera', stopSent: 'mapPin',
  sentDeclined: 'warningCircle', handover: 'users', liveAlert: 'warning', riderJoined: 'userPlus', riderLeft: 'user', removedFromBusiness: 'user',
};

function open(m: Model, x: Note) {
  const uid = m.business?.id;
  if (uid && !x.read) void updateDoc(doc(db, 'notifications', uid, 'items', x.id), { read: true }).catch(() => {});
  ui.set({ notes: false });
  track('notification_opened', { kind: x.kind || 'other' });
  if (['riderJoined', 'riderLeft', 'removedFromBusiness'].includes(x.kind)) return go('couriers');
  const routeId = typeof x.data.routeId === 'string' ? x.data.routeId : null;
  const stopId = typeof x.data.stopId === 'string' ? x.data.stopId : null;
  go('live');
  if (routeId && stopId && m.stop(`${routeId}/${stopId}`)) return openStop(`${routeId}/${stopId}`);
  if (routeId && m.route(routeId)) return openRoute(routeId);
  backToDay();
}

export function NotesPanel({ m }: { m: Model }) {
  const notes = m.data.notes;
  const unread = notes.filter((x) => !x.read);
  const readAll = () => {
    const uid = m.business?.id;
    if (!uid || !unread.length) return;
    const b = writeBatch(db);
    for (const x of unread) b.update(doc(db, 'notifications', uid, 'items', x.id), { read: true });
    void b.commit().catch(() => {});
  };
  return (
    <div className="notes" role="dialog" aria-label="Notifications">
      <div className="row" style={{ padding: '12px 12px 8px 16px' }}>
        <b className="grow">Notifications</b>
        {unread.length > 0 && (
          <button className="link cap" onClick={readAll}>
            Mark all read
          </button>
        )}
        <button className="iconbtn sm" aria-label="Close" onClick={() => ui.set({ notes: false })}>
          <Icon name="x" />
        </button>
      </div>
      <div className="rows notes-list">
        {notes.length ? (
          notes.map((x) => (
            <div key={x.id} className={`item${x.read ? '' : ' unread'}`} role="button" tabIndex={0} onClick={() => open(m, x)} onKeyDown={(e) => e.key === 'Enter' && open(m, x)}>
              <Icon name={ICONS[x.kind] ?? 'bell'} />
              <div className="grow" style={{ minWidth: 0 }}>
                <b>{x.title}</b>
                {x.body && <div className="cap">{x.body}</div>}
              </div>
              <span className="cap nw">{x.at ? (m.now - x.at < 3600_000 ? ago(x.at, m.now) : dayTime(x.at, m.now, m.zone)) : ''}</span>
            </div>
          ))
        ) : (
          <EmptyHelp
            title="No notifications yet."
            tries={[
              { icon: 'mapTrifold', label: 'Needs a look, on Live', note: 'What wants your attention today', act: 'live' },
              { icon: 'userPlus', label: 'Invite a courier', note: 'You’re told when they join', act: 'invite' },
            ]}
            onTry={(a) => {
              ui.set({ notes: false });
              if (a === 'live') {
                go('live');
                backToDay();
              } else void import('../app/dialogs').then((d) => d.openDialog({ kind: 'invite' }));
            }}
          />
        )}
      </div>
    </div>
  );
}
